import { Controller } from '@hotwired/stimulus';

const normalize = (value) =>
  value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Fuzzy match: exact substring, or all query characters appearing in order
// (subsequence) — tolerant to small typos / omissions.
const fuzzyMatch = (query, haystack) => {
  if (!query) return true;
  if (haystack.includes(query)) return true;

  let i = 0;
  for (const char of haystack) {
    if (char === query[i]) i += 1;
    if (i === query.length) return true;
  }
  return false;
};

// Client-side fuzzy filter for the variables list: accent/case-insensitive,
// typo-tolerant, with substring highlight, live count and ⌘K focus.
export default class extends Controller {
  static targets = [
    'input', 'clear', 'kbd', 'count', 'row', 'empty', 'emptyQuery',
  ];

  connect() {
    this.total = this.rowTargets.length;
    this.onKeydown = this.onKeydown.bind(this);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnect() {
    document.removeEventListener('keydown', this.onKeydown);
  }

  onKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.inputTarget.focus();
      this.inputTarget.select();
    }
  }

  filter() {
    const raw = this.inputTarget.value.trim();
    const query = normalize(raw);
    let visible = 0;

    this.rowTargets.forEach((row) => {
      const name = row.querySelector('.ds-var-name');
      const desc = row.querySelector('.ds-var-desc');
      const haystack = normalize(`${name?.textContent || ''} ${desc?.textContent || ''}`);
      const match = fuzzyMatch(query, haystack);

      row.hidden = !match;
      if (match) {
        visible += 1;
        this.highlight(name, raw);
        this.highlight(desc, raw);
      }
    });

    this.clearTarget.hidden = raw.length === 0;
    if (this.hasKbdTarget) this.kbdTarget.hidden = raw.length > 0;
    this.emptyTarget.hidden = visible > 0;
    if (this.hasEmptyQueryTarget) this.emptyQueryTarget.textContent = raw;
    this.updateCount(raw, visible);
  }

  highlight(element, raw) {
    if (!element) return;

    const text = element.textContent;
    if (!raw) {
      element.textContent = text;
      return;
    }

    const index = normalize(text).indexOf(normalize(raw));
    if (index === -1) {
      element.textContent = text;
      return;
    }

    const mark = document.createElement('mark');
    mark.className = 'ds-tpl-mark';
    mark.textContent = text.slice(index, index + raw.length);
    element.replaceChildren(
      document.createTextNode(text.slice(0, index)),
      mark,
      document.createTextNode(text.slice(index + raw.length)),
    );
  }

  updateCount(raw, visible) {
    const data = this.countTarget.dataset;
    this.countTarget.textContent = raw
      ? (data.labelFiltered || '').replace('COUNT', visible)
      : data.labelAll || '';
  }

  clear() {
    this.inputTarget.value = '';
    this.filter();
    this.inputTarget.focus();
  }
}
